import { useToast } from 'vuestic-ui'
import router from '../router'
import { useAuthStore } from '../stores/auth'

const friendlyErrorMap: Array<[RegExp, string]> = [
  [/invalid credentials/i, 'Email/username or password is incorrect.'],
  [/invalid refresh token/i, 'Your session expired. Please log in again.'],
  [/user no longer active/i, 'Your account is inactive. Please contact an admin.'],
  [/household not found/i, 'Household not found.'],
  [/billing cycle already exists/i, 'That billing cycle already exists.'],
  [/billing amount must be greater than zero/i, 'Please enter a billing amount greater than zero.'],
  [/payment submission already reviewed/i, 'This payment has already been reviewed.'],
  [/selected bills are required/i, 'Please select at least one bill.'],
  [/payment total is less than selected bills due/i, 'Payment amount is not enough for the selected bills.'],
  [/street already exists/i, 'Street name already exists.'],
  [/street has households assigned/i, 'This street still has households assigned.'],
  [/email is already in use/i, 'Email address is already in use.'],
  [/username is already in use/i, 'Username is already in use.'],
]

const mapFriendlyMessage = (message: string) => {
  for (const [pattern, friendly] of friendlyErrorMap) {
    if (pattern.test(message)) return friendly
  }
  return message
}

export const getErrorMessage = async (response: Response, fallback: string) => {
  try {
    const clone = response.clone()
    const contentType = clone.headers.get('content-type') || ''
    if (contentType.includes('application/json')) {
      const data = await clone.json()
      if (typeof data?.message === 'string') {
        return mapFriendlyMessage(data.message)
      }
      if (Array.isArray(data?.message)) {
        return mapFriendlyMessage(data.message[0])
      }
      if (Array.isArray(data?.errors) && data.errors.length > 0) {
        const first = data.errors[0]?.message
        if (typeof first === 'string') return mapFriendlyMessage(first)
      }
      // Some endpoints (e.g. the household seat-limit / free-tier-cap 402s) respond with
      // only `{ error, seatLimit }` or `{ error, upgrade }` — no `message`/`errors` field.
      // Handle that shape here instead of falling through to `clone.text()` below: the
      // body was already consumed by `clone.json()` above, so a second read on the same
      // clone throws "Body already read" and gets swallowed by the catch, silently
      // discarding the real message.
      if (typeof data?.error === 'string') {
        return mapFriendlyMessage(data.error)
      }
      return fallback
    }
    const text = await clone.text()
    if (text) return mapFriendlyMessage(text)
  } catch {
    // ignore parsing errors
  }
  return fallback
}

export const authFetch = async (input: RequestInfo, init: RequestInit = {}) => {
  const auth = useAuthStore()
  const token = auth.getAccessToken()
  const tenantCode = auth.getTenantCode()
  const headers = new Headers(init.headers || {})

  if (!(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`)
  }
  if (tenantCode && !headers.has('x-tenant-code')) {
    headers.set('x-tenant-code', tenantCode)
  }

  const response = await fetch(input, { ...init, headers })

  // Only an unauthenticated response (bad/expired token) should end the session. A 403 means the
  // user is authenticated but not allowed this specific resource (e.g. an auditor hitting an
  // admin-only endpoint) — surface it to the caller instead of logging them out of the whole app.
  if (response.status === 401) {
    auth.logout()
    if (router.currentRoute.value.name !== 'login') {
      // Flag the redirect as an expired session so the login page can explain why the user
      // was bounced here (rather than a silent redirect).
      router.push({ name: 'login', query: { expired: '1' } })
    }
  }

  // 402 = the tenant's plan doesn't unlock this feature ([RequiresCommunityPlan] on the
  // backend, or the free-tier household cap). This is a plan/billing state, not an auth
  // failure, so — unlike 401 — it must NOT log the user out. The route guard already keeps
  // a locked tenant off gated pages; this toast is the backstop for actions taken from a
  // free page (e.g. the household-cap create button) or a stale client that raced a
  // downgrade.
  if (response.status === 402) {
    const clone = response.clone()
    let detail: string | null = null
    let seatLimit = false
    try {
      const data = await clone.json()
      if (typeof data?.error === 'string') detail = data.error
      seatLimit = data?.seatLimit === true
    } catch {
      // ignore parsing errors — fall back to the generic message below
    }
    // A seat-limit 402 (tenant is already on Community but hit its paid seat cap) is not an
    // "upgrade to Community" situation — the page that triggered the request owns showing
    // that message (see HouseholdsPage.vue's seat-limit banner). Only the free-tier/locked
    // 402 gets this generic toast.
    if (!seatLimit) {
      const cta = 'Upgrade to Community to unlock this feature.'
      const message = detail && !/upgrade to community/i.test(detail) ? `${detail} ${cta}` : detail ?? cta
      useToast().init({ message, color: 'warning' })
    }
  }

  return response
}
